import React, { useEffect } from 'react';
import { Form, Row, Col, Alert, Accordion } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useNFe, useAtualizarTransporte } from '../../../../hooks/useNFe';
import {
  TransporteNFe,
  AtualizarTransporteRequest,
  NFeModalidadeFrete,
  getModalidadeFreteLabel,
} from '../../../../types/nfe';

interface SecaoTransporteProps {
  nfeId: string;
  isReadOnly: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}

const SecaoTransporte: React.FC<SecaoTransporteProps> = ({ nfeId, isReadOnly, onDirtyChange }) => {
  const { data: nfe } = useNFe(nfeId);
  const transporte = nfe?.transporte;
  const { showToast } = useToast();
  const mutation = useAtualizarTransporte(nfeId);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
  } = useForm<AtualizarTransporteRequest>({
    defaultValues: {
      modalidadeFrete: String(NFeModalidadeFrete.SemFrete),
    },
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (transporte) {
      reset({
        modalidadeFrete: transporte.modalidadeFrete ?? String(NFeModalidadeFrete.SemFrete),
        nomeTransportadora: transporte.nomeTransportadora,
        cnpjTransportadora: transporte.cnpjTransportadora,
        cpfTransportadora: transporte.cpfTransportadora,
        inscricaoEstadual: transporte.inscricaoEstadual,
        enderecoCompleto: transporte.endereco,
        nomeMunicipio: transporte.municipio,
        uf: transporte.uf,
        placaVeiculo: transporte.placa,
        ufVeiculo: transporte.ufVeiculo,
      });
    }
  }, [transporte, reset]);

  const modalidadeFrete = watch('modalidadeFrete');
  const showDadosAdicionais = modalidadeFrete !== String(NFeModalidadeFrete.SemFrete);

  const onSubmit = async (data: AtualizarTransporteRequest) => {
    try {
      await mutation.mutateAsync(data);
      showToast('Transporte atualizado com sucesso', 'success');
      reset(data);
    } catch (error: any) {
      console.error('Erro ao atualizar transporte:', error);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {mutation.isError && (
        <Alert
          variant="danger"
          className="border border-danger border-opacity-25 bg-danger-subtle mb-3"
        >
          <div className="text-danger-emphasis">
            Erro ao atualizar transporte. Por favor, tente novamente.
          </div>
        </Alert>
      )}

      <Row className="g-3">
        <Col xs={12}>
          <p className="text-body-tertiary small mb-3">
            Seção opcional para informar dados sobre o transporte da mercadoria.
          </p>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label className="text-body-secondary fw-semibold">
              Modalidade de Frete *
            </Form.Label>
            <Form.Select
              {...register('modalidadeFrete', { required: 'Modalidade é obrigatória' })}
              disabled={isReadOnly}
              isInvalid={!!errors.modalidadeFrete}
            >
              <option value={String(NFeModalidadeFrete.ContaRemetente)}>
                {getModalidadeFreteLabel(NFeModalidadeFrete.ContaRemetente)}
              </option>
              <option value={String(NFeModalidadeFrete.ContaDestinatario)}>
                {getModalidadeFreteLabel(NFeModalidadeFrete.ContaDestinatario)}
              </option>
              <option value={String(NFeModalidadeFrete.ContaTerceiros)}>
                {getModalidadeFreteLabel(NFeModalidadeFrete.ContaTerceiros)}
              </option>
              <option value={String(NFeModalidadeFrete.ProprioRemetente)}>
                {getModalidadeFreteLabel(NFeModalidadeFrete.ProprioRemetente)}
              </option>
              <option value={String(NFeModalidadeFrete.ProprioDestinatario)}>
                {getModalidadeFreteLabel(NFeModalidadeFrete.ProprioDestinatario)}
              </option>
              <option value={String(NFeModalidadeFrete.SemFrete)}>
                {getModalidadeFreteLabel(NFeModalidadeFrete.SemFrete)}
              </option>
            </Form.Select>
            {errors.modalidadeFrete && (
              <Form.Control.Feedback type="invalid">
                {errors.modalidadeFrete.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>

      {showDadosAdicionais && (
        <Accordion className="mt-3">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <span className="text-body-secondary">Dados da Transportadora (Opcional)</span>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="text-body-secondary fw-semibold">
                      Nome/Razão Social
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...register('nomeTransportadora')}
                      disabled={isReadOnly}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="text-body-secondary fw-semibold">CNPJ</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('cnpjTransportadora')}
                      disabled={isReadOnly}
                      placeholder="00.000.000/0000-00"
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label className="text-body-secondary fw-semibold">
                      Inscrição Estadual
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...register('inscricaoEstadual')}
                      disabled={isReadOnly}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <span className="text-body-secondary">Dados do Veículo (Opcional)</span>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="g-3">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="text-body-secondary fw-semibold">Placa</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('placaVeiculo')}
                      disabled={isReadOnly}
                      placeholder="ABC-1234"
                      maxLength={7}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label className="text-body-secondary fw-semibold">UF</Form.Label>
                    <Form.Select {...register('ufVeiculo')} disabled={isReadOnly}>
                      <option value="">-</option>
                      <option value="AC">AC</option>
                      <option value="AL">AL</option>
                      <option value="AP">AP</option>
                      <option value="AM">AM</option>
                      <option value="BA">BA</option>
                      <option value="CE">CE</option>
                      <option value="DF">DF</option>
                      <option value="ES">ES</option>
                      <option value="GO">GO</option>
                      <option value="MA">MA</option>
                      <option value="MT">MT</option>
                      <option value="MS">MS</option>
                      <option value="MG">MG</option>
                      <option value="PA">PA</option>
                      <option value="PB">PB</option>
                      <option value="PR">PR</option>
                      <option value="PE">PE</option>
                      <option value="PI">PI</option>
                      <option value="RJ">RJ</option>
                      <option value="RN">RN</option>
                      <option value="RS">RS</option>
                      <option value="RO">RO</option>
                      <option value="RR">RR</option>
                      <option value="SC">SC</option>
                      <option value="SP">SP</option>
                      <option value="SE">SE</option>
                      <option value="TO">TO</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}

      {!isReadOnly && (
        <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top border-opacity-25">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={!isDirty || mutation.isPending}
            loading={mutation.isPending}
            loadingPosition="start"
          >
            {mutation.isPending ? 'Salvando...' : 'Salvar Transporte'}
          </Button>
        </div>
      )}
    </Form>
  );
};

export default SecaoTransporte;
